import { apiRequest, apiUpload, apiRequestBlob } from '@/lib/api/client';
import type {
  StudentAcademicConnection,
  StudentAISuggestionResponse,
  StudentAssignment,
  StudentAttendanceOverview,
  StudentDiscipline,
  StudentDocumentTemplate,
  StudentGradeOverview,
  StudentGroupChatMessage,
  StudentLessonWeek,
  StudentLiteratureItem,
  StudentLiteratureSearchRequest,
  StudentMeeting,
  StudentOpportunity,
  StudentPortfolioItem,
  StudentProfile,
  StudentRequest,
  StudentSavedLiterature,
  StudentScientificProject,
  StudentSubmission,
  StudentWorkspaceSummary,
} from './types';

const apiClient = {
  get: <T>(path: string) => apiRequest<T>(path, { method: 'GET', channel: 'platform' }),
  post: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'POST', body, channel: 'platform' }),
  put: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'PUT', body, channel: 'platform' }),
  patch: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'PATCH', body, channel: 'platform' }),
  delete: <T>(path: string) => apiRequest<T>(path, { method: 'DELETE', channel: 'platform' }),
};

export const studentApi = {
  uploadCourseFile: (code: string, file: File) => apiUpload<{ id: string; url: string; filename: string }>(`/course-files/${encodeURIComponent(code)}`, file),
  downloadCourseFile: async (url: string) => {
    const { blob, filename } = await apiRequestBlob(url, { channel: 'platform' });
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a'); link.href = objectUrl; link.download = filename || 'work'; link.click();
    URL.revokeObjectURL(objectUrl);
  },
  getSummary: (): Promise<StudentWorkspaceSummary> =>
    apiClient.get<StudentWorkspaceSummary>('/course-student/summary'),

  getProfile: (): Promise<StudentProfile> =>
    apiClient.get<StudentProfile>('/course-student/profile'),

  updateProfile: (payload: { phone?: string; email?: string }): Promise<StudentProfile> =>
    apiClient.put<StudentProfile>('/course-student/profile', payload),

  getDisciplines: (): Promise<StudentDiscipline[]> =>
    apiClient.get<StudentDiscipline[]>('/course-student/disciplines'),

  getWeeks: (disciplineCode: string): Promise<StudentLessonWeek[]> =>
    apiClient.get<StudentLessonWeek[]>(`/course-student/weeks?discipline_code=${disciplineCode}`),

  getAssignments: (disciplineCode: string): Promise<StudentAssignment[]> =>
    apiClient.get<StudentAssignment[]>(`/course-student/assignments?discipline_code=${disciplineCode}`),

  submitAssignmentVersion: (
    assignmentId: string,
    payload: { attachment_id?: string; client_request_id?: string; feedback_note?: string }
  ): Promise<StudentSubmission> =>
    apiClient.post<StudentSubmission>(`/course-student/assignments/${assignmentId}/submissions`, payload),

  getGrades: (disciplineCode: string): Promise<StudentGradeOverview> =>
    apiClient.get<StudentGradeOverview>(`/course-student/grades?discipline_code=${disciplineCode}`),

  getAttendance: (disciplineCode: string): Promise<StudentAttendanceOverview> =>
    apiClient.get<StudentAttendanceOverview>(`/course-student/attendance?discipline_code=${disciplineCode}`),

  getDocuments: (): Promise<StudentDocumentTemplate[]> =>
    apiClient.get<StudentDocumentTemplate[]>('/course-student/documents'),

  searchLiterature: (req: StudentLiteratureSearchRequest): Promise<StudentLiteratureItem[]> =>
    apiClient.post<StudentLiteratureItem[]>('/course-student/literature/search', req),

  getSavedLiterature: (): Promise<StudentSavedLiterature[]> =>
    apiClient.get<StudentSavedLiterature[]>('/course-student/literature/saved'),

  saveLiterature: (payload: Partial<StudentSavedLiterature>): Promise<StudentSavedLiterature> =>
    apiClient.post<StudentSavedLiterature>('/course-student/literature/saved', payload),

  deleteSavedLiterature: (id: string): Promise<void> =>
    apiClient.delete(`/course-student/literature/saved/${id}`),

  getConnections: (): Promise<StudentAcademicConnection[]> =>
    apiClient.get<StudentAcademicConnection[]>('/course-student/connections'),

  getGroupChatMessages: (groupCode: string, disciplineCode?: string): Promise<StudentGroupChatMessage[]> =>
    apiClient.get<StudentGroupChatMessage[]>(`/course-student/group-chat?group_code=${encodeURIComponent(groupCode)}${disciplineCode ? `&discipline_code=${encodeURIComponent(disciplineCode)}` : ''}`),

  sendGroupChatMessage: (payload: { discipline_code?: string; group_code: string; message: string }): Promise<StudentGroupChatMessage> =>
    apiClient.post<StudentGroupChatMessage>('/course-student/group-chat', payload),

  getMeetings: (): Promise<StudentMeeting[]> =>
    apiClient.get<StudentMeeting[]>('/course-student/meetings'),

  getScientificProjects: (): Promise<StudentScientificProject[]> =>
    apiClient.get<StudentScientificProject[]>('/course-student/scientific-projects'),

  getPortfolio: (): Promise<StudentPortfolioItem[]> =>
    apiClient.get<StudentPortfolioItem[]>('/course-student/portfolio'),

  togglePortfolio: (payload: {
    submission_id?: string;
    title: string;
    work_type?: string;
    discipline_code?: string;
    filename?: string;
    score_label?: string;
  }): Promise<StudentPortfolioItem> =>
    apiClient.post<StudentPortfolioItem>('/course-student/portfolio/toggle', payload),

  getOpportunities: (): Promise<StudentOpportunity[]> =>
    apiClient.get<StudentOpportunity[]>('/course-student/opportunities'),

  updateOpportunity: (
    oppId: string,
    payload: { status_label?: string; is_tracked?: boolean }
  ): Promise<StudentOpportunity> =>
    apiClient.patch<StudentOpportunity>(`/course-student/opportunities/${oppId}`, payload),

  getRequests: (): Promise<StudentRequest[]> =>
    apiClient.get<StudentRequest[]>('/course-student/requests'),

  createRequest: (payload: {
    discipline_code: string;
    category: string;
    title: string;
    message: string;
  }): Promise<StudentRequest> =>
    apiClient.post<StudentRequest>('/course-student/requests', payload),

  getAISuggestion: (payload: { prompt: string; discipline_code?: string }): Promise<StudentAISuggestionResponse> =>
    apiClient.post<StudentAISuggestionResponse>('/course-student/ai/suggest', payload),
};
